import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabaseClient';
import { lessonStatusMeta } from '../utils/lessonStatus';
import ChatMessage from './ChatMessage';
import LessonBadgeModal from './LessonBadgeModal';
import SessionProgressDock from './SessionProgressDock';
import './sessionProgress.css';

const { FiClock, FiSend, FiZap } = FiIcons;
const SESSION_INCREMENT = 20;

const getTime = () => new Date().toLocaleTimeString([], {
  hour: 'numeric',
  minute: '2-digit'
});

function formatTimestamp(isoString) {
  if (!isoString) return getTime();
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return getTime();
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
}

async function getFunctionStatusCode(error) {
  const status = error?.context?.status;
  if (typeof status === 'number') return status;
  return null;
}

function userFriendlyError(status) {
  if (status === 400) return "This lesson isn't available yet.";
  if (status === 401 || status === 403) {
    return 'We could not verify access to this lesson right now.';
  }
  return 'Something went wrong. Please try again.';
}

function ChatPanel({
  child,
  lesson,
  onCompleteLesson,
  onLessonStatusChanged
}) {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [messages, setMessages] = useState([]);
  const [requestError, setRequestError] = useState('');
  const [completionError, setCompletionError] = useState('');
  const [sessionProgress, setSessionProgress] = useState(0);
  const [showBadge, setShowBadge] = useState(false);
  const threadEnd = useRef(null);

  useEffect(() => {
    setInput('');
    setIsTyping(false);
    setRequestError('');
    setCompletionError('');
    setSessionProgress(0);
    setShowBadge(false);
  }, [lesson?.id]);

  useEffect(() => {
    let cancelled = false;

    async function loadMessageHistory() {
      if (!child?.id || !lesson?.id) {
        setMessages([]);
        setIsLoadingHistory(false);
        return;
      }

      setIsLoadingHistory(true);
      setRequestError('');

      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          id,
          sender,
          content,
          created_at,
          sessions!inner (
            child_id,
            lesson_id
          )
        `)
        .eq('sessions.child_id', child.id)
        .eq('sessions.lesson_id', lesson.id)
        .order('created_at', { ascending: true });

      if (cancelled) return;

      if (error) {
        setMessages([]);
        setRequestError('Could not load this lesson conversation yet.');
        setIsLoadingHistory(false);
        return;
      }

      setMessages((data || []).map((row) => ({
        id: row.id,
        sender: row.sender,
        text: row.content || '',
        timestamp: formatTimestamp(row.created_at)
      })));
      setIsLoadingHistory(false);
    }

    loadMessageHistory();

    return () => {
      cancelled = true;
    };
  }, [child?.id, lesson?.id]);

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, isLoadingHistory]);

  useEffect(() => {
    if (sessionProgress === 100) {
      setShowBadge(true);
    }
  }, [sessionProgress]);

  const sendMessage = async (text) => {
    const cleanText = text.trim();
    if (!cleanText || isTyping || !child?.id || !lesson?.id) return;

    setInput('');
    setIsTyping(true);
    setRequestError('');

    const { data, error } = await supabase.functions.invoke('ai-tutor-chat', {
      body: {
        child_id: child.id,
        lesson_id: lesson.id,
        message: cleanText
      }
    });

    if (error) {
      const status = await getFunctionStatusCode(error);
      setRequestError(userFriendlyError(status));
      setIsTyping(false);
      return;
    }

    setMessages((current) => [
      ...current,
      {
        id: `${Date.now()}-student`,
        sender: 'student',
        text: cleanText,
        timestamp: getTime()
      },
      {
        id: `${Date.now()}-ai`,
        sender: 'ai',
        text: data?.reply || 'Let’s keep working through this together.',
        timestamp: getTime()
      }
    ]);
    setSessionProgress((current) => Math.min(current + SESSION_INCREMENT, 100));
    setIsTyping(false);
    try {
      await onLessonStatusChanged?.();
    } catch {
      // Keep chat responsive even if sidebar refresh fails.
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendMessage(input);
  };

  const handleStarterClick = async () => {
    if (!lesson?.description) return;
    await sendMessage(`Can you help me with: ${lesson.description}`);
  };

  const handleCompleteLesson = async (lessonId) => {
    try {
      setCompletionError('');
      await onCompleteLesson?.(lessonId);
    } catch {
      setCompletionError('Could not mark this lesson complete right now.');
      throw new Error('Could not mark complete.');
    }
  };

  const statusMeta = lesson
    ? lessonStatusMeta[lesson.status]
    : null;

  return (
    <>
      <section className="chat-panel">
        <div className="chat-session-header">
          <div className="chat-heading">
            <div>
              <span className="subject-label">{lesson?.subject || 'Lesson'} session</span>
              <h1>{lesson?.title || 'Select a lesson'}</h1>
            </div>

            <div className="chat-heading-meta">
              {statusMeta && (
                <span className={`lesson-status-pill ${statusMeta.className}`}>
                  <SafeIcon icon={statusMeta.icon} />
                  {statusMeta.label}
                </span>
              )}
              <span className="session-time">
                <SafeIcon icon={FiClock} />
                8 min
              </span>
            </div>
          </div>
        </div>

        <div className="chat-thread" aria-live="polite">
          {isLoadingHistory && (
            <motion.div
              animate={{ opacity: 1 }}
              className="typing-row"
              initial={{ opacity: 0 }}
            >
              <span />
              <span />
              <span />
              Loading conversation
            </motion.div>
          )}

          <AnimatePresence>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
          </AnimatePresence>

          {isTyping && (
            <motion.div
              animate={{ opacity: 1 }}
              className="typing-row"
              initial={{ opacity: 0 }}
            >
              <span />
              <span />
              <span />
              Nova is thinking
            </motion.div>
          )}
          <div ref={threadEnd} />
        </div>

        <div className="floating-progress-region">
          <SessionProgressDock
            lesson={lesson}
            progress={sessionProgress}
          />
        </div>

        <div className="chat-controls">
          <div className="chat-controls-top">
            <button
              className="prompt-starter"
              disabled={isTyping || !lesson}
              onClick={handleStarterClick}
              type="button"
            >
              <SafeIcon icon={FiZap} />
              {lesson ? `Help me with ${lesson.title}` : 'Select a lesson to begin'}
            </button>
          </div>

          <form className="chat-form" onSubmit={handleSubmit}>
            <input
              aria-label="Ask the tutor a question"
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask a question in your own words..."
              value={input}
            />
            <button
              aria-label="Send message"
              disabled={!input.trim() || isTyping || !lesson}
              type="submit"
            >
              <SafeIcon icon={FiSend} />
            </button>
          </form>

          {(requestError || completionError) && (
            <p className="chat-hint dashboard-error">
              {requestError || completionError}
            </p>
          )}

          <p className="chat-hint">
            Ask a question to start this lesson conversation.
          </p>
        </div>
      </section>

      <LessonBadgeModal
        isOpen={showBadge}
        lesson={lesson}
        onClose={() => setShowBadge(false)}
        onCompleteLesson={handleCompleteLesson}
      />
    </>
  );
}

export default ChatPanel;